import * as React from "react"
import { brandingSelector } from "../../../recoil/atoms/room"
import { useRecoilValue, useSetRecoilState } from "recoil"
import {
  Box,
  Flex,
  /* Image, */
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Button,
  Text
} from "@chakra-ui/react"
import { auctionParamsSelector } from "../../../recoil/atoms/auction"
import { OverflownText } from "../../common/overflown-text"
import { RiAuctionFill } from "react-icons/ri"
import { Icon } from "../../common/icons"
import { numberWithCommas, toPixelString } from "../../../util"
import { watchPartyScreenSelector } from "../../../recoil/atoms/watchPartyScreen"
import { AiOutlineClose } from "react-icons/ai"
import { useLocalUserType } from "../../../hooks/useLocalUserType"
import { showBadgeOverlayAtom } from "../../../recoil/atoms/overlay"
import { useTranslation } from "react-i18next"
import { localUserIdAtom } from "../../../recoil/atoms/users"

interface AuctionOverlayProps {
  id: string
  isMobile?: boolean
  width?: string | number
  isSmallMainScreen?: boolean
  isBadgeLiveMode?: boolean
  isTransperent?: boolean
  isMediumAuctionBadge?: boolean
  isTouch: boolean
  isLandscape: boolean
}

const AuctionIncrementType = window.MeetOnline.AuctionIncrementType

const _AuctionOverlay: React.FC<AuctionOverlayProps> = ({
  id,
  isMobile,
  isTransperent,
  isBadgeLiveMode,
  width,
  isSmallMainScreen,
  isMediumAuctionBadge,
  isTouch,
  isLandscape
}) => {
  const roomStyles = useRecoilValue(brandingSelector)
  const setAuctionParams = useSetRecoilState(auctionParamsSelector)
  const {
    title,
    description,
    currency,
    ask,
    bid,
    lastOnetoBid,
    incrementType
  } = useRecoilValue(auctionParamsSelector)
  const localUserId = useRecoilValue(localUserIdAtom)

  const disabledOpenModal = React.useMemo<boolean>(() => {
    return (
      (lastOnetoBid === localUserId &&
        (incrementType === AuctionIncrementType.AUTO_PERCENT ||
          incrementType === AuctionIncrementType.AUTO_FIXED)) ||
      +ask === 0 ||
      +ask >= 999999999.99
    )
  }, [ask, incrementType, lastOnetoBid, localUserId])

  const { t } = useTranslation()
  const setEnabledOverlay = useSetRecoilState(showBadgeOverlayAtom)
  const { isModerator } = useLocalUserType()
  const isWatchParty = useRecoilValue(watchPartyScreenSelector)

  const openModalBid = React.useCallback(
    (
      event:
        | React.MouseEvent<HTMLButtonElement>
        | React.TouchEvent<HTMLButtonElement>
    ): void => {
      event.stopPropagation()
      setAuctionParams({ isModalBid: true })
    },
    [setAuctionParams]
  )

  const [timeStamp, setTimeStamp] = React.useState({
    start: 0,
    end: 0
  })

  const handleTouchStart = (
    event: React.TouchEvent<HTMLButtonElement | HTMLDivElement>
  ): void => {
    setTimeStamp({ ...timeStamp, start: event.timeStamp })
  }

  const handleTouchEnd = React.useCallback(
    (event: React.TouchEvent<HTMLButtonElement>): void => {
      if (event.timeStamp - timeStamp.start < 150) {
        if (+ask !== 0 && +ask <= 999999999.99) {
          openModalBid(event)
        }
      }
      setTimeStamp({ start: 0, end: 0 })
    },
    [ask, openModalBid, timeStamp.start]
  )

  const fontSize = React.useMemo(() => {
    return isSmallMainScreen ? "xs" : isMediumAuctionBadge ? "sm" : "md"
  }, [isMediumAuctionBadge, isSmallMainScreen])

  const handleCloseOverlay = e => {
    setAuctionParams({ isShowOverlay: false })
    setEnabledOverlay(true)
    e.stopPropagation()
  }

  const handleTouchEndCloseOverlay = e => {
    if (e.timeStamp - timeStamp.start < 150) {
      handleCloseOverlay(e)
    }
    setTimeStamp({ start: 0, end: 0 })
  }

  return (
    <Stat
      id={id}
      px={{ base: 2, md: 2 }}
      py={isMobile ? "1" : "2"}
      borderWidth="1px"
      borderStyle="solid"
      borderLeft="none"
      borderColor={roomStyles.badgeBorderColor ?? "gray.300"}
      bg={
        isTransperent
          ? "rgba(0, 0, 0, 0.2)"
          : roomStyles.badgeBackgroundColor ?? "white"
      }
      rounded={"lg"}
      roundedBottomLeft={!isBadgeLiveMode && "none"}
      roundedTopLeft={!isBadgeLiveMode && "none"}
      maxW={`calc(${width} + 20px)`}
    >
      {!isBadgeLiveMode && !isModerator && (
        <Flex
          background={roomStyles.badgeBackgroundColor ?? "white"}
          pos="absolute"
          width="19px"
          height="19px"
          borderRadius="50%"
          right="-9px"
          top="-9px"
          alignItems="center"
          justifyContent="center"
          border="1px solid"
          borderColor={roomStyles.badgeBorderColor ?? "#CBD5E0"}
          cursor={"pointer"}
          zIndex={1}
          onClick={isTouch ? null : handleCloseOverlay}
          onTouchStart={isTouch ? handleTouchStart : null}
          onTouchEnd={isTouch ? handleTouchEndCloseOverlay : null}
        >
          <Icon
            src={AiOutlineClose}
            fontSize={"0.8rem"}
            marginLeft="1px"
            color={roomStyles?.badgeTextColor ?? "#000"}
          />
        </Flex>
      )}
      <Flex>
        <Box my={"auto"} alignContent={"center"}>
          <Icon
            src={RiAuctionFill}
            fontSize={"50px"}
            display="block"
            color={roomStyles?.badgeTextColor && roomStyles?.badgeTextColor}
            overflow="hidden"
            whiteSpace="nowrap"
            textOverflow="ellipsis"
          />
        </Box>
        {/*  )} */}
        <Flex
          flexDirection="column"
          width={isBadgeLiveMode && "100%"}
          color={isTransperent && "white"}
        >
          <Box
            pl="0.5rem"
            w="100%"
            display="grid"
            fontFamily={
              roomStyles?.badgeFontFamily && roomStyles?.badgeFontFamily
            }
            color={roomStyles?.badgeTextColor && roomStyles?.badgeTextColor}
          >
            <StatLabel
              fontWeight="medium"
              fontSize={
                isSmallMainScreen ? "xs" : isMediumAuctionBadge ? "sm" : "md"
              }
              isTruncated
              textOverflow="ellipsis"
              overflow="hidden"
              whiteSpace="nowrap"
            >
              {title}
            </StatLabel>
            <StatHelpText
              maxHeight="85px"
              noOfLines={2}
              textOverflow="ellipsis"
              overflow="hidden"
              whiteSpace="nowrap"
              display="block"
              marginBottom={"0px"}
              fontSize={
                isSmallMainScreen ? "xs" : isMediumAuctionBadge ? "sm" : "md"
              }
              lineHeight={1.5}
            >
              <OverflownText>{description}</OverflownText>
            </StatHelpText>
          </Box>
          <Flex
            mt="auto"
            alignContent={"end"}
            justifyContent="space-between"
            fontFamily={
              roomStyles?.badgeFontFamily && roomStyles?.badgeFontFamily
            }
          >
            <Flex
              paddingLeft="0.5rem"
              margin="auto 0 auto 0"
              flexDirection="column"
            >
              <Flex
                color={roomStyles?.badgeTextColor && roomStyles?.badgeTextColor}
              >
                <Flex flexDirection="column">
                  <Text
                    display="inline"
                    fontSize={fontSize}
                    fontWeight={"bold"}
                    marginRight="0.5rem"
                    opacity="0.75"
                  >
                    {isMediumAuctionBadge || isSmallMainScreen
                      ? t("Bid")
                      : t(`Current bid`)}
                  </Text>
                  <Text
                    display="inline"
                    fontSize={fontSize}
                    fontWeight={"bold"}
                    marginRight="0.5rem"
                    whiteSpace="nowrap"
                  >
                    {isMediumAuctionBadge || isSmallMainScreen
                      ? t("Ask")
                      : t("Asking price")}
                  </Text>
                </Flex>
                <Flex flexDirection="column" opacity="0.75">
                  <StatNumber
                    fontSize={fontSize}
                    fontWeight={"bold"}
                    display="inline"
                    mr={"0.6rem"}
                    /* ml={isMediumAuctionBadge ? "0.2rem" : isSmallMainScreen ? "0.45rem" : "0.56rem"} */
                    textOverflow="ellipsis"
                    overflow="hidden"
                    whiteSpace="nowrap"
                  >
                    {currency}{" "}
                    {bid === 0 ? "—" : numberWithCommas((+bid).toFixed(2))}
                  </StatNumber>
                  <StatNumber
                    fontSize={fontSize}
                    fontWeight={"bold"}
                    display="inline"
                    mr={"0.6rem"}
                    textOverflow="ellipsis"
                    overflow="hidden"
                    whiteSpace="nowrap"
                  >
                    {currency}{" "}
                    {ask === 0 ? "—" : numberWithCommas((+ask).toFixed(2))}
                  </StatNumber>
                </Flex>
              </Flex>
            </Flex>
            <Button
              onClick={isTouch ? null : openModalBid}
              onTouchStart={
                isTouch || (isWatchParty && isLandscape)
                  ? handleTouchStart
                  : null
              }
              onTouchEnd={
                isTouch || (isWatchParty && isLandscape) ? handleTouchEnd : null
              }
              colorscheme="teal"
              marginTop="auto"
              variant="outline"
              size={isSmallMainScreen ? "xs" : "sm"}
              _focus={{}}
              fontSize={
                isSmallMainScreen ? "xs" : isMediumAuctionBadge ? "sm" : "md"
              }
              padding={isMobile && "10px"}
              whiteSpace="nowrap"
              lineHeight="1"
              height={isMobile && "20px"}
              bg={
                roomStyles?.badgeButtonBackgroundColor
                  ? roomStyles.badgeButtonBackgroundColor
                  : "gray.100"
              }
              textColor={
                roomStyles?.badgeButtonTextColor
                  ? roomStyles.badgeButtonTextColor
                  : "#718096"
              }
              _hover={{
                backgroundColor: roomStyles?.badgeButtonMouseoverBackgroundColor
                  ? roomStyles.badgeButtonMouseoverBackgroundColor
                  : "#edf2f7",
                textColor: roomStyles?.badgeButtonMouseoverTextColor
                  ? roomStyles.badgeButtonMouseoverTextColor
                  : "#000",
                borderColor: roomStyles?.badgeButtonBorderMouseoverColor || null,
              }}
              borderColor={
                roomStyles?.badgeButtonBorderColor
                  ? roomStyles.badgeButtonBorderColor
                  : "#e2e8f0"
              }
              borderRadius={toPixelString(roomStyles?.badgeButtonBorderRadius)}
              fontFamily={
                roomStyles?.badgeButtonFontFamily
                  ? roomStyles.badgeButtonFontFamily
                  : "inherit"
              }
              disabled={disabledOpenModal}
            >
              {t("Place Bid")}
            </Button>
          </Flex>
        </Flex>
      </Flex>
    </Stat>
  )
}

export const AuctionOverlay = React.memo(_AuctionOverlay)
